import React from "react";
import { Box, Typography, Button, Paper, Chip, Divider, Avatar, CircularProgress  } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SendIcon from "@mui/icons-material/Send";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

// ── Helpers ──────────────────────────────────────────────────────────────────

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
    <Typography sx={{ color: "#6B7280", fontSize: "13px", flexShrink: 0, mr: 2 }}>{label}</Typography>
    <Typography sx={{ color: "#111827", fontSize: "13px", fontWeight: 600, textAlign: "right" }}>
      {value || "—"}
    </Typography>
  </Box>
);

const SectionCard = ({ title, icon, children, accentColor = "#8B5E3C" }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: "16px",
      border: "1px solid #E5E7EB",
      bgcolor: "#FFFFFF",
      overflow: "hidden",
      mb: 3,
    }}
  >
    {/* Section Header */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 3,
        py: 2,
        borderBottom: "1px solid #F3F4F6",
        bgcolor: "#FAFAFA",
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "8px",
          bgcolor: `${accentColor}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 17, color: accentColor } })}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.8px", color: "#374151" }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 3, py: 2.5 }}>{children}</Box>
  </Paper>
);

// Company avatar initials
const CompanyAvatar = ({ name }) => {
  const initials = name
    ? name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
    : "?";
  // deterministic hue from name
  const hue = name ? [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360 : 200;
  return (
    <Avatar
      sx={{
        width: 36,
        height: 36,
        fontSize: "12px",
        fontWeight: 700,
        bgcolor: `hsl(${hue},55%,88%)`,
        color: `hsl(${hue},55%,35%)`,
        flexShrink: 0,
        border: `2px solid hsl(${hue},55%,78%)`,
      }}
    >
      {initials}
    </Avatar>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const Step3Preview = ({ onBack, onSend, settingsData, companiesData, reportData, isSubmitting = false }) => {
  const uniqueCompanies = companiesData
    ? [...new Map(companiesData.map(c => [c.company, c])).values()]
    : [];

  const getPeriodLabel = () => {
    if (!settingsData) return "—";
    if (settingsData.monthLabel) return `${settingsData.monthLabel} ${settingsData.annual}`;
    if (settingsData.quarterLabel) return `${settingsData.quarterLabel} ${settingsData.annual}`;
    if (settingsData.annual) return String(settingsData.annual);
    if (settingsData.startDate && settingsData.endDate)
      return `${settingsData.startDate} → ${settingsData.endDate}`;
    return "—";
  };

  const requestTypeLabel =
    settingsData?.request_type === 1 ? "Monthly"
      : settingsData?.request_type === 2 ? "Quarterly"
        : settingsData?.request_type === 3 ? "Annual"
          : settingsData?.request_type === 4 ? "Custom"
            : "—";

  //const metrics = reportData?.metrics?.filter(m => m.type) || [];
  const metrics = reportData?.metrics?.filter(m => m.label) || [];
  const customFields = reportData?.customFields?.filter(f => f.field_name) || [];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#F9FAFB",
        pb: 10,
      }}
    >
      {/* ── Top banner ── */}
      


      {/* ── Content grid ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "320px 1fr",
            lg: "360px 1fr 280px",   // 3 columns on large screens
          },
          gap: 3,
          maxWidth: "1600px",
          mx: "auto",
          px: { xs: 2, md: 3 },
          pt: 1,
        }}
      >
        {/* Preview headline — spans all columns */}
        <Box
          sx={{
            gridColumn: "1 / -1",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            pb: 1,
            borderBottom: "1px solid #E5E7EB",
            mb: 1,
          }}
        >
          <Chip
            label="PREVIEW"
            size="small"
            sx={{
              bgcolor: "#EFF6FF",
              color: "#1D4ED8",
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "0.8px",
              borderRadius: "6px",
            }}
          />
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#111827" }}>
            {settingsData?.report_name || "Report Preview"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "12px", color: "#9CA3AF" }}>
            Read-only · {uniqueCompanies.length} {uniqueCompanies.length === 1 ? "company" : "companies"}
          </Typography>
        </Box>
        {/* ── LEFT: Request + Companies ── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

          {/* Request summary */}
          <SectionCard
            title="Request Summary"
            icon={<SettingsOutlinedIcon />}
            accentColor="#0369A1"
          >
            <InfoRow label="Report name" value={settingsData?.report_name} />
            <InfoRow label="Period" value={getPeriodLabel()} />
            <InfoRow
              label="Due date"
              value={
                settingsData?.due_date
                  ? new Date(settingsData.due_date).toLocaleDateString("en-GB", {
                    day: "2-digit", month: "short", year: "numeric",
                  })
                  : "—"
              }
            />
            <InfoRow label="Request type" value={requestTypeLabel} />
          </SectionCard>

          {/* Companies */}
          <SectionCard
            title={`Companies (${uniqueCompanies.length})`}
            icon={<BusinessIcon />}
            accentColor="#059669"
          >
            {uniqueCompanies.length === 0 ? (
              <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>
                No companies selected
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {uniqueCompanies.map((c, i) => (
                  <Box key={i}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CompanyAvatar name={c.company} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#111827",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {c.company || "—"}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3, flexWrap: "wrap" }}>
                          {c.partners?.[0]?.partner_name && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                              <PersonOutlineIcon sx={{ fontSize: 11, color: "#9CA3AF" }} />
                              <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>
                                {c.partners[0].partner_name}
                              </Typography>
                            </Box>
                          )}
                          {c.grade && (
                            <Chip
                              label={`Grade ${c.grade}`}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: "10px",
                                fontWeight: 600,
                                bgcolor: "#F0FDF4",
                                color: "#166534",
                                "& .MuiChip-label": { px: 0.8 },
                              }}
                            />
                          )}
                          {c.location && (
                            <Typography sx={{ fontSize: "11px", color: "#9CA3AF" }}>
                              · {c.location}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                    {i < uniqueCompanies.length - 1 && (
                      <Divider sx={{ mt: 1.5, borderColor: "#F3F4F6" }} />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
        </Box>

        {/* ── CENTRE: Documents + KPI + News + Custom ── */}
        <Box>

          {/* Documents */}
          <SectionCard
            title="Documents"
            icon={<UploadFileIcon />}
            accentColor="#7C3AED"
          >
            {reportData?.documentsDescription ? (
              <Box
                sx={{
                  bgcolor: "#F5F3FF",
                  border: "1px solid #DDD6FE",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 2,
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#4B5563", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {reportData.documentsDescription}
                </Typography>
              </Box>
            ) : (
              <EmptyState text="No document description provided." />
            )}
          </SectionCard>

          {/* KPI */}
          <SectionCard
            title="KPI's Verifying"
            icon={<BarChartOutlinedIcon />}
            accentColor="#D97706"
          >
            {metrics.length === 0 ? (
              <EmptyState text="No KPI metrics selected." />
            ) : (
              <Box>
                {/* Table header */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 2fr",
                    gap: 2,
                    px: 1.5,
                    py: 1,
                    bgcolor: "#FFFBEB",
                    borderRadius: "8px",
                    mb: 1,
                  }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    KPI
                  </Typography>
                  <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    Description
                  </Typography>
                </Box>
                {/* Rows */}
                {metrics.map((metric, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 2fr",
                      gap: 2,
                      px: 1.5,
                      py: 1.5,
                      borderBottom: i < metrics.length - 1 ? "1px solid #F3F4F6" : "none",
                      "&:hover": { bgcolor: "#FFFBEB50" },
                      borderRadius: "6px",
                      transition: "background 0.15s",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "#D97706",
                          flexShrink: 0,
                        }}
                      />
                      <Typography sx={{ fontSize: "14px", color: "#111827", fontWeight: 600 }}>
                        {metric.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "13px", color: "#6B7280", lineHeight: 1.5 }}>
                      {metric.description || "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>

          {/* News */}
          <SectionCard
            title="Company News"
            icon={<BoltOutlinedIcon />}
            accentColor="#DC2626"
          >
            {reportData?.newsDescription ? (
              <Box
                sx={{
                  bgcolor: "#FFF5F5",
                  border: "1px solid #FECACA",
                  borderRadius: "10px",
                  px: 2.5,
                  py: 2,
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#4B5563", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {reportData.newsDescription}
                </Typography>
              </Box>
            ) : (
              <EmptyState text="No news description provided." />
            )}
          </SectionCard>

          {/* Custom Fields */}
          {customFields.length > 0 && (
            <SectionCard
              title="Custom Fields"
              icon={<SettingsOutlinedIcon />}
              accentColor="#0369A1"
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {customFields.map((field, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 2,
                      bgcolor: "#F0F9FF",
                      border: "1px solid #BAE6FD",
                      borderRadius: "10px",
                    }}
                  >
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#0C4A6E", mb: 0.5 }}>
                      {field.field_name}
                    </Typography>
                    {field.field_description && (
                      <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                        {field.field_description}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </SectionCard>
          )}
        </Box>

        {/* ── RIGHT: Stats panel (lg only) ── */}
        <Box sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "column", gap: 3 }}>
          <StatCard
            label="Total Companies"
            value={uniqueCompanies.length}
            color="#0369A1"
            bg="#EFF6FF"
          />
          <StatCard
            label="KPI Metrics"
            value={metrics.length}
            color="#D97706"
            bg="#FFFBEB"
          />
          <StatCard
            label="Custom Fields"
            value={customFields.length}
            color="#7C3AED"
            bg="#F5F3FF"
          />

          {/* Grade breakdown */}
          {uniqueCompanies.some(c => c.grade) && (
            <Paper
              variant="outlined"
              sx={{ borderRadius: "16px", border: "1px solid #E5E7EB", p: 2.5 }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.6px", mb: 2 }}>
                Grade Breakdown
              </Typography>
              {["A", "B", "C", "B+", "A+"].map(grade => {
                const count = uniqueCompanies.filter(c => c.grade === grade).length;
                if (count === 0) return null;
                return (
                  <Box key={grade} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Chip
                      label={grade}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "11px",
                        fontWeight: 700,
                        bgcolor: "#F0FDF4",
                        color: "#166534",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#374151" }}>
                      {count}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          )}
        </Box>
      </Box>

      {/* ── Fixed footer ── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: { xs: 0, md: 240 },   // ← clear the 240px sidebar
          right: 0,
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E5E7EB",
          px: { xs: 2, md: 4 },
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 1000,
        }}
      >
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            color: "#374151",
            borderColor: "#E5E7EB",
            fontWeight: 600,
            px: 2.5,
            "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB", color: "black" },
          }}
        >
          Back to Edit
        </Button>

        <Button
          variant="contained"
          endIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: "white" }} /> : <SendIcon />}
          onClick={onSend}     // ← wire up the handler
          disabled={isSubmitting}
          sx={{
            bgcolor: "#1E517B",
            textTransform: "none",
            borderRadius: "20px",
            px: 4,
            fontWeight: 600,
            "&:hover": { bgcolor: "#153d5d" },
            "&.Mui-disabled": { bgcolor: "#1E517B", opacity: 0.7 },
          }}
        >
          {isSubmitting ? "Sending..." : "Send"}
        </Button>
      </Box>
    </Box>
  );
};

// ── Small sub-components ──────────────────────────────────────────────────────

const EmptyState = ({ text }) => (
  <Box
    sx={{
      py: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "#F9FAFB",
      borderRadius: "10px",
      border: "1px dashed #E5E7EB",
    }}
  >
    <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>{text}</Typography>
  </Box>
);

const StatCard = ({ label, value, color, bg }) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: "16px",
      border: `1px solid ${color}30`,
      bgcolor: bg,
      p: 2.5,
      textAlign: "center",
    }}
  >
    <Typography sx={{ fontSize: "36px", fontWeight: 800, color, lineHeight: 1 }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "12px", color: "#6B7280", mt: 0.5, fontWeight: 500 }}>
      {label}
    </Typography>
  </Paper>
);

export default Step3Preview;